import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Link as LinkIcon } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Spinner } from "@/components/Spinner";
import { EventCategoryFormDialog } from "./EventCategoryFormDialog";
import { listEventCategories, deleteEventCategory } from "@/lib/api/admin";
import type { EventCategory, EventCategoryVisibilityScope } from "@/lib/api/types";

// Same env-detection convention as Sidebar.tsx's IS_PROD.
const SITE_URL =
  import.meta.env.VITE_FIREBASE_PROJECT_ID === "disco-fb-pro"
    ? "https://disca.app"
    : "https://main-website-disca-sta.web.app";

type ManageEventCategoriesDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  storeSlug?: string;
};

// Which link(s) show follows the category's own scope; a missing/unrecognized value falls back to "all" (both), never neither.
function categoryLinks(
  storeSlug: string | undefined,
  urlParam: string,
  visibilityScope: EventCategoryVisibilityScope | undefined
) {
  if (!storeSlug) return [];
  const scope: EventCategoryVisibilityScope =
    visibilityScope === "public" || visibilityScope === "unlisted" ? visibilityScope : "all";
  const suffix = `?category=${urlParam}`;
  const links: { label: string; url: string }[] = [];
  if (scope === "unlisted" || scope === "all") {
    links.push({ label: "Unlisted", url: `${SITE_URL}/business/ul/${storeSlug}${suffix}` });
  }
  if (scope === "public" || scope === "all") {
    links.push({ label: "Public", url: `${SITE_URL}/business/pb/${storeSlug}${suffix}` });
  }
  return links;
}

export function ManageEventCategoriesDialog({
  open,
  onOpenChange,
  storeId,
  storeSlug,
}: ManageEventCategoriesDialogProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<EventCategory | undefined>(undefined);
  const [deleting, setDeleting] = useState<EventCategory | null>(null);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["event-categories", storeId],
    queryFn: () => listEventCategories(user!, storeId),
    enabled: !!user && open,
  });

  const deleteMut = useMutation({
    mutationFn: (categoryId: string) => deleteEventCategory(user!, storeId, categoryId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["event-categories", storeId] });
      setDeleting(null);
      toast.success("Category deleted.");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Categories</DialogTitle>
          </DialogHeader>

          {isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner className="size-6 border-2" />
            </div>
          ) : categories.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No categories yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {categories.map((cat) => {
                const links = categoryLinks(storeSlug, cat.url_param, cat.visibility_scope);
                return (
                  <li key={cat.id} className="space-y-1 py-3">
                    <div className="flex items-start gap-3">
                      {cat.banner_url ? (
                        <img
                          src={cat.banner_url}
                          alt=""
                          className="size-10 rounded object-cover shrink-0"
                        />
                      ) : (
                        <div className="size-10 rounded bg-muted shrink-0" />
                      )}
                      <p className="flex-1 min-w-0 text-sm font-medium truncate self-center">
                        {cat.name}
                      </p>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={() => {
                            setEditing(cat);
                            setFormOpen(true);
                          }}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={() => setDeleting(cat)}
                        >
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    <div className="pl-[52px] space-y-1">
                      {links.length > 0 ? (
                        links.map((link) => (
                          <div key={link.label} className="flex items-center gap-1.5">
                            <span className="text-[10px] font-medium uppercase text-muted-foreground shrink-0 w-16">
                              {link.label}
                            </span>
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline truncate flex-1 min-w-0"
                            >
                              ?category={cat.url_param}
                            </a>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-6 shrink-0"
                              title={`Copy ${link.label.toLowerCase()} link`}
                              onClick={async () => {
                                await navigator.clipboard.writeText(link.url);
                                toast.success("Link copied to clipboard.");
                              }}
                            >
                              <LinkIcon className="size-3.5" />
                            </Button>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-muted-foreground truncate">
                          ?category={cat.url_param}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setEditing(undefined);
              setFormOpen(true);
            }}
          >
            <Plus className="size-4" />
            Add Category
          </Button>
        </DialogContent>
      </Dialog>

      <EventCategoryFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        storeId={storeId}
        category={editing}
      />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete category?"
        description={`"${deleting?.name}" will be removed. Events using it keep their other fields but lose this category.`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMut.isPending}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
    </>
  );
}
